import { logger } from 'shared/lib'
import { ComplianceApi } from 'entities/Compliance/api/compliance'

export enum EnumCheckUrlStatus {
  Success = 1,
  Warning = 2,
  Error = 3,
}

type ICheckSingleUrlResult = {
  url: string
  reachable: boolean
  domainCheck: boolean
  status: EnumCheckUrlStatus
  isSuccess: boolean
  isWarning: boolean
  isError: boolean
}

export const checkSingleUrl = async (url: string): Promise<ICheckSingleUrlResult> => {
  try {
    const { data } = await ComplianceApi.onlineFormUrlCheck({ onlineFormUrl: url })

    let status: EnumCheckUrlStatus
    switch (data.status) {
      case 'reachable':
        status = EnumCheckUrlStatus.Success
        break
      case 'unverified':
        status = EnumCheckUrlStatus.Warning
        break
      case 'unreachable':
      default:
        status = EnumCheckUrlStatus.Error
        break
    }

    if (!data.reachable) {
      status = EnumCheckUrlStatus.Error
    } else if (data.http_status === null) {
      status = EnumCheckUrlStatus.Error
    }

    return {
      url: url,
      reachable: data.reachable,
      domainCheck: data.domain_check,
      status: status,
      isError: status === EnumCheckUrlStatus.Error,
      isSuccess: status === EnumCheckUrlStatus.Success,
      isWarning: status === EnumCheckUrlStatus.Warning,
    }
  } catch (error) {
    logger.error(error)
    return {
      url: url,
      reachable: false,
      domainCheck: false,
      status: EnumCheckUrlStatus.Error,
      isError: true,
      isSuccess: false,
      isWarning: false,
    }
  }
}

type IComplianceCheckUrlResult = {
  url: string
  isSuccess: boolean
  isWarning: boolean
  isError: boolean
}

export const complianceCheckUrl = async (url: string): Promise<IComplianceCheckUrlResult> => {
  const isBaseUrlHasProtocol = url.startsWith('http://') || url.startsWith('https://')

  try {
    if (isBaseUrlHasProtocol) {
      const result = await checkSingleUrl(url)

      return {
        url: url,
        isError: result.isError,
        isWarning: result.isWarning,
        isSuccess: result.isSuccess,
      }
    }

    const httpUrl = `http://${url}`
    const httpsUrl = `https://${url}`

    const [httpsResult, httpResult] = await Promise.allSettled([
      checkSingleUrl(httpsUrl),
      checkSingleUrl(httpUrl),
    ])

    const httpsReachable = httpsResult.status === 'fulfilled' && !httpsResult.value.isError
    const httpReachable = httpResult.status === 'fulfilled' && !httpResult.value.isError

    if (httpsReachable) {
      return {
        url: httpsUrl,
        isError: httpsResult.value.isError,
        isWarning: httpsResult.value.isWarning,
        isSuccess: httpsResult.value.isSuccess,
      }
    } else if (httpReachable) {
      return {
        url: httpUrl,
        isError: httpResult.value.isError,
        isWarning: httpResult.value.isWarning,
        isSuccess: httpResult.value.isSuccess,
      }
    }

    return {
      url: '',
      isError: true,
      isWarning: false,
      isSuccess: false,
    }
  } catch (error) {
    logger.error(error)

    return {
      url: '',
      isError: true,
      isWarning: false,
      isSuccess: false,
    }
  }
}
